import { Platform } from 'react-native';
import { Alert } from 'react-native';

// Maximum file size in bytes (50 MB)
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB in bytes

// Calculate file size from Data URL (web)
const getDataUrlSize = (dataUrl: string): number => {
  try {
    // Data URL format: data:[<mediatype>][;base64],<data>
    const base64Data = dataUrl.split(',')[1];
    if (!base64Data) return 0;
    
    // Base64 encoding increases size by ~33%, so we need to decode to get actual size
    // For base64: actual_size = (base64_length * 3) / 4
    // But we can also count padding characters
    const padding = (base64Data.match(/=/g) || []).length;
    const actualSize = (base64Data.length * 3) / 4 - padding;
    return Math.floor(actualSize);
  } catch (error) {
    console.error('Error calculating Data URL size:', error);
    return 0;
  }
};

// File size validation function
// On web, can accept an optional fileSize parameter to avoid Data URL parsing
export const validateFileSize = async (fileUri: string, fileSizeParam?: number): Promise<{ isValid: boolean; size: number; errorMessage?: string }> => {
  try {
    let fileSize = 0;
    
    if (Platform.OS === 'web') {
      // If fileSize is provided (from File object), use it directly
      if (fileSizeParam !== undefined && fileSizeParam > 0) {
        fileSize = fileSizeParam;
      } else if (fileUri.startsWith('data:')) {
        // On web, fileUri is a Data URL (data:image/jpeg;base64,...)
        fileSize = getDataUrlSize(fileUri);
      } else {
        // If it's not a Data URL, try to fetch it
        try {
          const response = await fetch(fileUri, { method: 'HEAD' });
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            fileSize = parseInt(contentLength, 10);
          } else {
            // Fallback: fetch the full file to get size
            const blob = await fetch(fileUri).then(r => r.blob());
            fileSize = blob.size;
          }
        } catch (fetchError) {
          console.error('Error fetching file size:', fetchError);
          // If fetch fails, estimate from Data URL if possible
          if (fileUri.startsWith('data:')) {
            fileSize = getDataUrlSize(fileUri);
          } else {
            throw new Error('Unable to determine file size');
          }
        }
      }
    } else {
      // On native, use RNFS
      const RNFS = require('react-native-fs').default;
      const fileStats = await RNFS.stat(fileUri);
      fileSize = fileStats.size;
    }
    
    if (fileSize === 0) {
      return {
        isValid: false,
        size: 0,
        errorMessage: 'Unable to determine file size. Please try again.'
      };
    }
    
    if (fileSize > MAX_FILE_SIZE) {
      const sizeInMB = (fileSize / (1024 * 1024)).toFixed(2);
      const maxSizeInMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
      
      return {
        isValid: false,
        size: fileSize,
        errorMessage: `File size (${sizeInMB} MB) exceeds the maximum allowed size of ${maxSizeInMB} MB. Please select a smaller file.`
      };
    }
    
    return {
      isValid: true,
      size: fileSize
    };
  } catch (error) {
    console.error('Error validating file size:', error);
    return {
      isValid: false,
      size: 0,
      errorMessage: 'Unable to determine file size. Please try again.'
    };
  }
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Show file size error alert
export const showFileSizeError = (errorMessage: string) => {
  Alert.alert(
    'File Too Large',
    errorMessage,
    [{ text: 'OK', style: 'default' }]
  );
};

// Show file size success info
export const showFileSizeInfo = (fileSize: number) => {
  const sizeInMB = (fileSize / (1024 * 1024)).toFixed(2);
  // File size info logged
};
